import React, { useEffect, useState } from "react";
import { getSales, saveSales, exportSalesToExcel } from "../../components/Api/GmsSales";
import { getProducts } from "../../components/Api/GmsProduct";
import { getEmployees } from "../../components/Api/GmsEmployee";
import Select from "react-select";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Card, CardHeader, CardBody, Container, Row, Col } from "reactstrap";
import Swal from "sweetalert2";
import { Download } from "lucide-react";
import 'bootstrap/dist/css/bootstrap.min.css';
import { 
  hasPagePermissionWithAdmin, 
  hasViewOnlyPermission, 
  hasEditOrHigherPermission, 
  hasDeletePermissionWithAdmin, 
  isUserAdmin 
} from "../../components/Permission/PermissionService";
import Breadcrumb from "../../components/Common/Breadcrumb";

const defaultForm = {
  sales_Id: 0,
  employee_Id: null,
  product_Id: null,
  transaction_Date: new Date().toISOString().split('T')[0],
  quantity: '',
  value: 0,
  amount: 0,
  created_By: 1,
  created_Date: new Date().toISOString(),
};

const emptyFilter = {
  employee_Id: null,
  product_Id: null,
  fromDate: null,
  toDate: null,
};

// Initialize with null dates to get all records
const getInitialFilter = () => {
  return {
    ...emptyFilter,
    fromDate: null,
    toDate: null
  };
};

export default function SalesbookPage() {
  const [sales, setSales] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [filter, setFilter] = useState(getInitialFilter());
  
  const [showModal, setShowModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalAmount, setTotalAmount] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Check permissions
  const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
  const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsSalesbook');
  const isViewOnly = hasViewOnlyPermission(permissions, '/GmsSalesbook');
  const hasEditOrHigher = hasEditOrHigherPermission(permissions, '/GmsSalesbook');
  const hasDeletePermission = hasDeletePermissionWithAdmin(permissions, '/GmsSalesbook');
  const isAdmin = isUserAdmin();
  
  // Get current user's employee ID (same as DaybookPage)
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const currentEmployeeId = currentUser.user_EmployeeId || currentUser.employeeId;
  
  // Determine if employee filter should be shown
  const showEmployeeFilter = !isViewOnly || isAdmin || (isViewOnly && !currentEmployeeId);

  // Session management
  const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    return {
      userId: authUser.userId || 1,
      userName: authUser.userName || "System",
    };
  };

  useEffect(() => {
    if (hasPermission) {
      loadSales(filter, page);
      loadEmployees();
      loadProducts();
    } else {
      window.location.href = '/Dashboard';
    }
  }, [hasPermission, page]);

  const loadEmployees = async () => {
    try {
      const data = await getEmployees({});
      setEmployees(data);
    } catch (error) {
      console.error("Error loading employees:", error);
    }
  };

  const loadProducts = async () => {
    try {
      const data = await getProducts({});
      setProducts(data);
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };

  const loadSales = async (filterData = filter, pageNumber = page) => {
    setLoading(true);
    try {
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filterData.employee_Id !== null && filterData.employee_Id !== undefined && { employee_Id: filterData.employee_Id }),
        ...(filterData.product_Id !== null && filterData.product_Id !== undefined && { product_Id: filterData.product_Id }),
        ...(filterData.fromDate && { fromDate: filterData.fromDate }),
        ...(filterData.toDate && { toDate: filterData.toDate })
      };
      
      // For users without edit permission, filter by their employee ID only (same as DaybookPage)
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }
      
      const data = await getSales(cleanFilter);
      // Sort by newest sales_Id first (latest inserted record)
      const sortedData = data.sort((a, b) => b.sales_Id - a.sales_Id);
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = sortedData.slice(startIndex, endIndex);
      setSales(paginatedData);
      setTotalCount(sortedData.length);
      
      // Calculate total amount from all filtered data
      const totalAmountValue = sortedData.reduce((total, sale) => total + sale.amount, 0);
      setTotalAmount(totalAmountValue);
    } catch (error) {
      console.error("Error loading sales:", error);
      Swal.fire("Error", "Failed to load sales: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    setForm({
      ...form,
      [field]: value,
    });
  };

  const handleEmployeeChange = (selectedOption) => {
    setForm({
      ...form,
      employee_Id: selectedOption ? selectedOption.value : null,
    });
  };

  const handleProductChange = (selectedOption) => {
    const selectedProduct = products.find(p => p.product_Id === selectedOption?.value);
    const newValue = selectedProduct ? selectedProduct.product_CostPrice : 0;
    const newAmount = newValue * form.quantity;
    
    setForm({
      ...form,
      product_Id: selectedOption ? selectedOption.value : null,
      value: newValue,
      amount: newAmount,
    });
  };

  const handleQuantityChange = (quantity) => {
    // Allow empty string for clearing the field
    if (quantity === '') {
      setForm({
        ...form,
        quantity: '',
        amount: 0,
      });
      return;
    }
    
    const newQuantity = parseFloat(quantity) || 0;
    const newAmount = form.value * newQuantity;
    
    setForm({
      ...form,
      quantity: newQuantity,
      amount: newAmount,
    });
  };

  const handleDateChange = (e) => {
    const newDate = e.target.value;
    setSelectedDate(newDate);
    setForm(prevForm => ({
      ...prevForm,
      transaction_Date: newDate,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.employee_Id || !form.product_Id) {
      Swal.fire("Error", "Please select both employee and product", "error");
      return;
    }

    if (!form.quantity || form.quantity === '' || parseFloat(form.quantity) <= 0) {
      Swal.fire("Error", "Please enter a valid quantity greater than 0", "error");
      return;
    }

    setSaveLoading(true);
    try {
      const session = getUserSession();
      const salesData = {
        ...form,
        created_By: session.userId,
        created_Date: new Date().toISOString(),
      };
      
      const result = await saveSales(salesData);
      Swal.fire("Success", result.message || "Sales transaction saved successfully", "success");
      
      // Reset only product dropdown and related fields, keep employee and date for next entry
      setForm({
        ...form,
        product_Id: null,
        quantity: '',
        value: 0,
        amount: 0,
      });
      
      // Don't refresh here - only refresh when modal closes
    } catch (error) {
      Swal.fire("Error", "Failed to save sales transaction: " + error.message, "error");
    } finally {
      setSaveLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilter({
      ...filter,
      [field]: value,
    });
  };

  const handleApplyFilter = () => {
    setPage(1);
    loadSales(filter);
  };

  const handleClearFilter = () => {
    const newFilter = getInitialFilter();
    setFilter(newFilter);
    setPage(1);
    loadSales(newFilter);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleString();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleExportExcel = async () => {
    try {
      setLoading(true);
      // Clean filter - remove null values and convert to proper types
      const cleanFilter = {
        ...(filter.employee_Id !== null && filter.employee_Id !== undefined && { employee_Id: filter.employee_Id }),
        ...(filter.product_Id !== null && filter.product_Id !== undefined && { product_Id: filter.product_Id }),
        ...(filter.fromDate && { fromDate: filter.fromDate }),
        ...(filter.toDate && { toDate: filter.toDate })
      };
      
      // For users without edit permission, filter by their employee ID only
      if (!hasEditOrHigher && currentEmployeeId) {
        cleanFilter.employee_Id = currentEmployeeId;
      }
      
      await exportSalesToExcel(cleanFilter);
      Swal.fire("Success", "Salesbook report exported successfully", "success");
    } catch (error) {
      console.error("Error exporting sales:", error);
      Swal.fire("Error", "Failed to export salesbook report", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setForm({
      ...defaultForm,
      transaction_Date: new Date().toISOString().split('T')[0]
    });
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setShowModal(true);
  };

  const getEmployeeName = (employeeId) => {
    const employee = employees.find(e => e.employee_Id === employeeId);
    return employee ? employee.employee_NameEn : employeeId;
  };

  const getProductName = (productId) => {
    const product = products.find(p => p.product_Id === productId);
    return product ? product.product_NameEn : productId;
  };



  if (!hasPermission) {
    return <div>Access Denied</div>;
  }

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb title="Salesbook Management" breadcrumbItem="Spa" />
        
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h4 className="mb-0">Salesbook Management</h4>
          <Button 
            color="primary" 
            onClick={handleExportExcel}
            disabled={loading || totalCount === 0}
          >
            <Download className="me-1" size={16} />
            Export Excel
          </Button>
        </div>

        {/* Filter Section */}
        <Card className="mb-4">
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Filter Sales</h5>
              <Button
                color="primary"
                onClick={() => setShowFilters(!showFilters)}
              >
                <i className="bx bx-filter me-2"></i>
                {showFilters ? "Hide Filters" : "Show Filters"}
              </Button>
            </div>
          </CardHeader>
          {showFilters && (
            <CardBody>
              <Row className="g-3 mb-3">
                {showEmployeeFilter && (
                  <Col md={3}>
                    <label className="form-label">Employee</label>
                    <Select
                      value={employees.find(e => e.employee_Id === filter.employee_Id) 
                        ? { value: filter.employee_Id, label: employees.find(e => e.employee_Id === filter.employee_Id).employee_NameEn }
                        : null}
                      onChange={(option) => handleFilterChange('employee_Id', option ? option.value : null)}
                      options={employees.map(employee => ({
                        value: employee.employee_Id,
                        label: employee.employee_NameEn
                      }))}
                      isClearable
                      isSearchable
                      placeholder="Select Employee..."
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (provided) => ({
                          ...provided,
                          backgroundColor: '#fff',
                          borderColor: '#ced4da',
                          color: '#495057'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: '#495057'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                          color: state.isSelected ? '#fff' : '#495057',
                          opacity: 1
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: 9999
                        })
                      }}
                      maxMenuHeight={200}
                    />
                  </Col>
                )}
                <Col md={3}>
                  <label className="form-label">Product</label>
                  <Select
                    value={products.find(p => p.product_Id === filter.product_Id) 
                      ? { value: filter.product_Id, label: products.find(p => p.product_Id === filter.product_Id).product_NameEn }
                      : null}
                    onChange={(option) => handleFilterChange('product_Id', option ? option.value : null)}
                    options={products.map(product => ({
                      value: product.product_Id,
                      label: product.product_NameEn
                    }))}
                    isClearable
                    isSearchable
                    placeholder="Select Product..."
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.fromDate || ''}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value || null)}
                  />
                </Col>
                <Col md={3}>
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={filter.toDate || ''}
                    onChange={(e) => handleFilterChange('toDate', e.target.value || null)}
                  />
                </Col>
              </Row>
              <div className="d-flex justify-content-end">
                <Button 
                  color="primary" 
                  className="me-2" 
                  onClick={handleApplyFilter} 
                  disabled={loading}
                >
                  <i className="bx bx-search me-2"></i>
                  Apply Filter
                </Button>
                <Button 
                  color="warning" 
                  onClick={handleClearFilter}
                >
                  <i className="bx bx-refresh me-2"></i>
                  Clear Filter
                </Button>
              </div>
            </CardBody>
          )}
        </Card>

        {/* Table Section */}
        <Card>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="card-title mb-0">Sales List ({totalCount})</h5>
              <div className="text-end">
                <div className="fw-bold text-success" style={{fontSize: '1.1rem'}}>
                  Total Amount: {totalAmount.toFixed(2)}
                </div>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>Employee</th>
                      <th>Product</th>
                      <th>Transaction Date</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="text-center">No sales transactions found</td>
                      </tr>
                    ) : (
                      sales.map((sale, index) => (
                        <tr key={sale.sales_Id}>
                          <td>{(page - 1) * pageSize + index + 1}</td>
                          <td>{getEmployeeName(sale.employee_Id)}</td>
                          <td>{getProductName(sale.product_Id)}</td>
                          <td>{formatDate(sale.transaction_Date)}</td>
                          <td>{sale.quantity}</td>
                          <td>{sale.value.toFixed(2)}</td>
                          <td>{sale.amount.toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            <div className="d-flex justify-content-between align-items-center mt-4 p-3" 
                 style={{backgroundColor: '#f8f9fa', borderRadius: '0.375rem', border: '1px solid #dee2e6'}}>
              <div className="text-muted" style={{fontSize: '14px', fontWeight: '500'}}>
                <i className="mdi mdi-information-outline me-2" style={{color: '#556ee6'}}></i>
                Showing <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount > 0 ? (page - 1) * pageSize + 1 : 0}</span> to <span style={{color: '#556ee6', fontWeight: '600'}}>{Math.min(page * pageSize, totalCount)}</span> of <span style={{color: '#556ee6', fontWeight: '600'}}>{totalCount}</span> entries
              </div>
              {totalCount > pageSize && (
                <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === 1}
                    onClick={() => handlePageChange(page - 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === 1 ? '#f8f9fa' : '#fff',
                      color: page === 1 ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-left"></i>
                  </Button>
                  
                  {[...Array(Math.ceil(totalCount / pageSize))].map((_, index) => {
                    const pageNum = index + 1;
                    const isActive = page === pageNum;
                    
                    return (
                      <Button
                        key={pageNum}
                        color={isActive ? "primary" : "light"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        style={{
                          border: '1px solid #dee2e6',
                          borderRadius: '0.375rem',
                          minWidth: '32px',
                          height: '32px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: isActive ? '#556ee6' : '#fff',
                          borderColor: isActive ? '#556ee6' : '#dee2e6',
                          color: isActive ? '#fff' : '#495057',
                          fontWeight: isActive ? '600' : '400',
                          boxShadow: isActive ? '0 2px 4px rgba(85,110,230,0.25)' : 'none'
                        }}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  
                  <Button
                    color="light"
                    size="sm"
                    disabled={page === Math.ceil(totalCount / pageSize)}
                    onClick={() => handlePageChange(page + 1)}
                    style={{
                      border: '1px solid #dee2e6',
                      borderRadius: '0.375rem',
                      minWidth: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: page === Math.ceil(totalCount / pageSize) ? '#f8f9fa' : '#fff',
                      color: page === Math.ceil(totalCount / pageSize) ? '#6c757d' : '#495057'
                    }}
                  >
                    <i className="mdi mdi-chevron-right"></i>
                  </Button>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Add Sales Transaction Modal */}
        <Modal isOpen={showModal} toggle={() => {
          setShowModal(false);
          setPage(1); // Reset to page 1 to see new records
          loadSales(filter); // Refresh data when modal closes
        }} size="lg">
          <ModalHeader toggle={() => {
            setShowModal(false);
            setPage(1); // Reset to page 1 to see new records
            loadSales(filter); // Refresh data when modal closes
          }}>
            Add Sales Transaction
          </ModalHeader>
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <Row className="g-3">
                <Col md={6}>
                  <label className="form-label">Employee *</label>
                  <Select
                    value={employees.find(e => e.employee_Id === form.employee_Id) 
                      ? { value: form.employee_Id, label: employees.find(e => e.employee_Id === form.employee_Id).employee_NameEn }
                      : null}
                    onChange={handleEmployeeChange}
                    options={employees.map(employee => ({
                      value: employee.employee_Id,
                      label: employee.employee_NameEn
                    }))}
                    isClearable
                    placeholder="Select Employee..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className="form-control"
                    value={selectedDate}
                    onChange={handleDateChange}
                    required
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Product *</label>
                  <Select
                    value={products.find(p => p.product_Id === form.product_Id) 
                      ? { value: form.product_Id, label: products.find(p => p.product_Id === form.product_Id).product_NameEn }
                      : null}
                    onChange={handleProductChange}
                    options={products.map(product => ({
                      value: product.product_Id,
                      label: product.product_NameEn
                    }))}
                    isClearable
                    placeholder="Select Product..."
                    isSearchable
                    menuPortalTarget={document.body}
                    styles={{
                      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: '#fff',
                        borderColor: '#ced4da',
                        color: '#495057'
                      }),
                      singleValue: (provided) => ({
                        ...provided,
                        color: '#495057'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : '#fff',
                        color: state.isSelected ? '#fff' : '#495057',
                        opacity: 1
                      }),
                      menu: (provided) => ({
                        ...provided,
                        zIndex: 9999
                      })
                    }}
                    maxMenuHeight={200}
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Unit Price</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={form.value}
                    onChange={(e) => handleFormChange('value', parseFloat(e.target.value) || 0)}
                    readOnly
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Quantity *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-control"
                    value={form.quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    placeholder="Enter quantity"
                    required
                  />
                </Col>
                <Col md={6}>
                  <label className="form-label">Total Amount</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={form.amount}
                    readOnly
                  />
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button 
                color="success" 
                type="submit" 
                disabled={saveLoading}
              >
                <i className="bx bx-check-circle me-2"></i>
                {saveLoading ? "Saving..." : "Save Transaction"}
              </Button>
              <Button 
                color="secondary" 
                onClick={() => {
                  setShowModal(false);
                  setPage(1); // Reset to page 1 to see new records
                  loadSales(filter); // Refresh data when modal closes
                }}
              >
                <i className="bx bx-x-circle me-2"></i>
                Close
              </Button>
            </ModalFooter>
          </form>
        </Modal>
      </Container>
    </div>
  );
} 